import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

/**
 * "이 단가를 회사 단가표로 저장" 확인 다이얼로그 - window.confirm 대신 페이지 안에서
 * 직접 값을 다시 한 번 보고 고칠 수 있게 한다(요구사항: "saves the materials actually
 * used and edited"). 열릴 때마다 지금 쓰는 재질 단가표 + 공통값(시간당 가공비/마진율)을
 * 복사해 내부 상태로 들고, 저장을 누르면 그 값(수정했다면 수정된 값)을 그대로 넘긴다.
 */
const CadQuoteSavePricesDialog = ({ open, materials, hourlyRate, marginRate, saving, onCancel, onSave }) => {
  const [rows, setRows] = useState([]);
  const [hourly, setHourly] = useState(hourlyRate);
  const [margin, setMargin] = useState(marginRate);

  useEffect(() => {
    if (!open) return;
    setRows(Object.entries(materials || {}).map(([code, m]) => ({
      mtrlCd: code,
      mtrlNm: m.mtrlNm || code,
      density: m.density,
      pricePerKg: m.pricePerKg,
      roughRate: m.roughRate,
      finishRate: m.finishRate,
    })));
    setHourly(hourlyRate);
    setMargin(marginRate);
  }, [open, materials, hourlyRate, marginRate]);

  const handleRowChange = (code, key, value) => {
    setRows((prev) => prev.map((r) => (r.mtrlCd === code ? { ...r, [key]: Number(value) } : r)));
  };

  const handleSave = () => {
    const materialList = rows.map((r, idx) => ({ ...r, sortOrd: idx }));
    onSave(materialList, {
      hourlyRate: Number(hourly),
      marginRate: Number(margin),
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>회사 단가표로 저장</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          지금 쓰는 재질 단가/가공속도와 공통값을 회사 단가표로 저장합니다. 필요하면 여기서
          바로 고친 뒤 저장하세요 - 앞으로 새 견적을 열 때 이 값이 기본값이 됩니다.
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>재질</TableCell>
                <TableCell align="right">밀도(g/cm³)</TableCell>
                <TableCell align="right">kg단가(원)</TableCell>
                <TableCell align="right">황삭율(cm³/분)</TableCell>
                <TableCell align="right">정삭율(cm²/분)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.mtrlCd}>
                  <TableCell>{row.mtrlCd}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={row.density}
                      onChange={(e) => handleRowChange(row.mtrlCd, 'density', e.target.value)}
                      sx={{ width: 90 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={row.pricePerKg}
                      onChange={(e) => handleRowChange(row.mtrlCd, 'pricePerKg', e.target.value)}
                      sx={{ width: 100 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={row.roughRate}
                      onChange={(e) => handleRowChange(row.mtrlCd, 'roughRate', e.target.value)}
                      sx={{ width: 90 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={row.finishRate}
                      onChange={(e) => handleRowChange(row.mtrlCd, 'finishRate', e.target.value)}
                      sx={{ width: 90 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <TextField
            type="number"
            size="small"
            label="시간당 가공비(원)"
            value={hourly}
            onChange={(e) => setHourly(e.target.value)}
          />
          <TextField
            type="number"
            size="small"
            label="마진율(%)"
            value={Math.round(Number(margin) * 100)}
            onChange={(e) => setMargin(Number(e.target.value) / 100)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>취소</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CadQuoteSavePricesDialog;
